import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Refresh,
  Delete,
  ExpandMore,
  ExpandLess,
  Info,
} from '@mui/icons-material';
import { GeocodingService } from '../../services/geocoding.service';
import { GeocodingCache } from '../../services/geocoding-cache.service';

export const GeocodingStats: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [expanded, setExpanded] = useState(false);

  const loadStats = () => {
    const service = GeocodingService.getInstance();
    setStats(service.getStats());
  };

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 2000);
    return () => clearInterval(interval);
  }, []);

  if (!stats) {
    return null;
  }

  const usagePercentage = (stats.requestCount / stats.dailyLimit) * 100;
  const isNearLimit = usagePercentage > 80;

  const handleClearCache = () => {
    if (window.confirm('Êtes-vous sûr de vouloir vider le cache de géocodage ?')) {
      GeocodingCache.clear();
      loadStats();
    }
  };

  const handleResetCounter = () => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser le compteur quotidien ?')) {
      const service = GeocodingService.getInstance();
      service.resetDailyCounter();
      loadStats();
    }
  };

  return (
    <Card
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        maxWidth: 400,
        zIndex: 1000,
        boxShadow: 3,
      }}
    >
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Geocoding Stats
          </Typography>
          <Box>
            <IconButton size="small" onClick={loadStats}>
              <Refresh fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
        </Box>

        <Stack spacing={1}>
          {/* Usage Progress */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                API Calls Today
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {stats.requestCount} / {stats.dailyLimit}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(usagePercentage, 100)}
              color={isNearLimit ? 'error' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>

          {/* Cache Stats */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`Cache: ${stats.cacheStats.localStorageSize}`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Queue: ${stats.queueLength}`}
              size="small"
              color={stats.queueLength > 10 ? 'warning' : 'default'}
              variant="outlined"
            />
          </Box>

          <Collapse in={expanded}>
            <Divider sx={{ my: 1 }} />
            
            {/* Detailed Stats */}
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Detailed Information
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Memory Cache
                  </Typography>
                  <Typography variant="body2">
                    {stats.cacheStats.memoryCacheSize} entries
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Remaining Calls
                  </Typography>
                  <Typography variant="body2" color={isNearLimit ? 'error' : 'text.primary'}>
                    {stats.remainingRequests}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Reset Time
                  </Typography>
                  <Typography variant="body2">
                    {new Date(stats.resetTime).toLocaleTimeString()}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Cache Hit Rate
                  </Typography>
                  <Typography variant="body2">
                    ~{Math.round((stats.cacheStats.localStorageSize / (stats.cacheStats.localStorageSize + stats.requestCount)) * 100)}%
                  </Typography>
                </Box>
              </Box>

              {isNearLimit && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Proche de la limite quotidienne d'appels API
                  </Typography>
                </Alert>
              )}

              {/* Actions */}
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleClearCache}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Clear Cache
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleResetCounter}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Reset Counter
                </Button>
              </Stack>

              <Alert severity="info" sx={{ mt: 1 }} icon={<Info />}>
                <Typography variant="caption">
                  Le cache persiste pendant 30 jours. Les appels sont limités à 2500/jour (Google Maps gratuit).
                </Typography>
              </Alert>
            </Stack>
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};